import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ComingSoon from '../components/common/ComingSoon';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Paper,
  Divider,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Fade,
  Grow,
  Slide,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Ballot as BallotIcon,
  HowToVote as VoteIcon,
  Assessment as ResultsIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  BarChart as BarChartIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend, 
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// API hooks
import { useGetPresentationsQuery } from '../services/api';

// Redux
import { useAppSelector } from '../hooks/redux';
import { Presentation } from '../services/slices/presentationSlice';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// Mock data for impressive demo
const mockAnalytics = {
  totalVotes: 2847,
  activeUsers: 156,
  completionRate: 94.2,
  avgResponseTime: 2.3,
  weeklyGrowth: 23.5,
  monthlyEngagement: [
    { month: 'Jan', votes: 1200, users: 89 },
    { month: 'Feb', votes: 1450, users: 102 },
    { month: 'Mar', votes: 1680, users: 118 },
    { month: 'Apr', votes: 1920, users: 134 },
    { month: 'May', votes: 2150, users: 145 },
    { month: 'Jun', votes: 2380, users: 156 },
  ],
  topPerformers: [
    { name: 'Sarah Chen', votes: 45, avatar: 'SC', role: 'Senior Partner' },
    { name: 'Michael Rodriguez', votes: 42, avatar: 'MR', role: 'Investment Director' },
    { name: 'Emily Watson', votes: 38, avatar: 'EW', role: 'Principal' },
    { name: 'David Kim', votes: 35, avatar: 'DK', role: 'Associate' },
  ],
  recentActivity: [
    { action: 'New presentation created', user: 'Sarah Chen', time: '2 minutes ago', type: 'create' },
    { action: 'Voting completed', user: 'Michael Rodriguez', time: '5 minutes ago', type: 'vote' },
    { action: 'Results published', user: 'Emily Watson', time: '12 minutes ago', type: 'results' },
    { action: 'User joined session', user: 'David Kim', time: '18 minutes ago', type: 'join' },
  ]
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAppSelector((state) => state.auth);
  const [animationStep, setAnimationStep] = useState(0);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [comingSoonFeature, setComingSoonFeature] = useState('');
  
  // Fetch presentations
  const { data: presentations, isLoading } = useGetPresentationsQuery(undefined, {
    skip: !user,
  });
  
  // Animation sequence - removed to prevent flashing
  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setAnimationStep(prev => (prev + 1) % 4);
  //   }, 3000);
  //   return () => clearInterval(timer);
  // }, []);
  
  // Filter presentations
  const activePresentation = presentations?.find((p: Presentation) => p.status === 'active');
  const recentPresentations = presentations
    ?.filter((p: Presentation) => p.status !== 'active')
    .sort((a: Presentation, b: Presentation) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);
  
  const isAdmin = user?.attributes?.['custom:role'] === 'admin';

  // Chart configurations
  const engagementChartData = {
    labels: mockAnalytics.monthlyEngagement.map(d => d.month),
    datasets: [
      {
        label: 'Total Votes',
        data: mockAnalytics.monthlyEngagement.map(d => d.votes),
        backgroundColor: alpha(theme.palette.primary.main, 0.8),
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
        borderRadius: 8,
      },
      {
        label: 'Active Users',
        data: mockAnalytics.monthlyEngagement.map(d => d.users),
        backgroundColor: alpha(theme.palette.secondary.main, 0.8),
        borderColor: theme.palette.secondary.main,
        borderWidth: 2,
        borderRadius: 8,
      }
    ],
  };

  const participationData = {
    labels: ['Completed', 'In Progress', 'Pending'],
    datasets: [{
      data: [78, 15, 7],
      backgroundColor: [
        alpha(theme.palette.success.main, 0.8),
        alpha(theme.palette.warning.main, 0.8),
        alpha(theme.palette.error.main, 0.8),
      ],
      borderWidth: 0,
    }],
  };

  const trendData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [{
      label: 'Engagement Score',
      data: [85, 89, 92, 96],
      borderColor: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      tension: 0.4,
      fill: true,
      pointBackgroundColor: theme.palette.primary.main,
      pointBorderColor: '#fff',
      pointBorderWidth: 3,
      pointRadius: 6,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.divider, 0.1),
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const StatCard = ({ title, value, subtitle, icon, color, trend }: any) => (
    <Grow in timeout={1000}>
      <Card 
        sx={{ 
          height: '100%',
          background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
          border: `1px solid ${alpha(color, 0.2)}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 8px 25px ${alpha(color, 0.3)}`,
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              backgroundColor: alpha(color, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {icon}
            </Box>
            {trend && (
              <Chip 
                label={`+${trend}%`} 
                size="small" 
                color="success"
                icon={<TrendingUpIcon />}
              />
            )}
          </Box>
          <Typography variant="h3" fontWeight="bold" color={color} gutterBottom>
            {value}
          </Typography>
          <Typography variant="h6" color="text.primary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        </CardContent>
      </Card>
    </Grow>
  );

  return (
    <Container maxWidth="xl">
      {/* Hero Section */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              mb: 2
            }}
          >
            Investment Team Dashboard
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Real-time insights into your team's decision-making process
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.attributes?.given_name || 'User'} • {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
      </Fade>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Votes Cast"
            value={mockAnalytics.totalVotes.toLocaleString()}
            subtitle="Across all presentations"
            icon={<VoteIcon sx={{ color: theme.palette.primary.main }} />}
            color={theme.palette.primary.main}
            trend={mockAnalytics.weeklyGrowth}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Users"
            value={mockAnalytics.activeUsers}
            subtitle="Currently online"
            icon={<PeopleIcon sx={{ color: theme.palette.success.main }} />}
            color={theme.palette.success.main}
            trend={15.2}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completion Rate"
            value={`${mockAnalytics.completionRate}%`}
            subtitle="Average across sessions"
            icon={<CheckCircleIcon sx={{ color: theme.palette.info.main }} />}
            color={theme.palette.info.main}
            trend={8.7}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Response Time"
            value={`${mockAnalytics.avgResponseTime}s`}
            subtitle="Per question"
            icon={<SpeedIcon sx={{ color: theme.palette.warning.main }} />}
            color={theme.palette.warning.main}
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Slide direction="up" in timeout={1200}>
        <Paper 
          sx={{ 
            p: 4, 
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrophyIcon color="primary" />
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            {isAdmin && (
              <Grid item>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/presentations/new')}
                  sx={{
                    borderRadius: 3,
                    px: 3,
                    py: 1.5,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                    }
                  }}
                >
                  Create New Presentation
                </Button>
              </Grid>
            )}
            <Grid item>
              <Button
                variant="outlined"
                size="large"
                startIcon={<BallotIcon />}
                onClick={() => navigate('/presentations')}
                sx={{ borderRadius: 3, px: 3, py: 1.5 }}
              >
                View All Presentations
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ResultsIcon />}
                onClick={() => {
                  setComingSoonFeature('Advanced Analytics Dashboard');
                  setComingSoonOpen(true);
                }}
                sx={{ borderRadius: 3, px: 3, py: 1.5 }}
              >
                Analytics Dashboard
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Slide>

      <Grid container spacing={4}>
        {/* Active Presentation */}
        <Grid item xs={12} lg={8}>
          <Fade in timeout={1400}>
            <Card sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: 100,
                  height: 100,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
                  borderRadius: '0 0 0 100px',
                }}
              />
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PlayArrowIcon sx={{ color: theme.palette.success.main }} />
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    Active Presentation
                  </Typography>
                  <Chip 
                    label="LIVE" 
                    color="success" 
                    variant="filled"
                    sx={{ 
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 1 },
                        '50%': { opacity: 0.5 },
                        '100%': { opacity: 1 },
                      }
                    }}
                  />
                </Box>
                
                {activePresentation ? (
                  <Box>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
                      {activePresentation.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: '1.1rem' }}>
                      {activePresentation.description}
                    </Typography>
                    
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <PeopleIcon color="primary" />
                          <Typography variant="body1">
                            <strong>Presenter:</strong> {activePresentation.presenter}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <ScheduleIcon color="primary" />
                          <Typography variant="body1">
                            <strong>Date:</strong> {new Date(activePresentation.date).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <BallotIcon color="primary" />
                          <Typography variant="body1">
                            <strong>Questions:</strong> {activePresentation.questions.length}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrendingUpIcon color="success" />
                          <Typography variant="body1">
                            <strong>Participation:</strong> 94% (146/156 users)
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <LinearProgress 
                      variant="determinate" 
                      value={94} 
                      sx={{ 
                        mb: 3, 
                        height: 8, 
                        borderRadius: 4,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 4,
                          background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                        }
                      }} 
                    />
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<VoteIcon />}
                        onClick={() => navigate(`/vote/${activePresentation.id}`)}
                        sx={{
                          borderRadius: 3,
                          px: 4,
                          py: 1.5,
                          background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                          '&:hover': {
                            background: `linear-gradient(45deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`,
                          }
                        }}
                      >
                        Join Voting Session
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<ResultsIcon />}
                          onClick={() => navigate(`/results/${activePresentation.id}`)}
                          sx={{ borderRadius: 3, px: 4, py: 1.5 }}
                        >
                          View Live Results
                        </Button>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <PauseIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Active Presentations
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Create a new presentation to get started
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Engagement Analytics */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>
            {/* Participation Breakdown */}
            <Grid item xs={12}>
              <Grow in timeout={1600}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BarChartIcon color="primary" />
                      Participation Breakdown
                    </Typography>
                    <Box sx={{ height: 200, position: 'relative' }}>
                      <Doughnut 
                        data={participationData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                          },
                        }} 
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>

            {/* Engagement Trend */}
            <Grid item xs={12}>
              <Grow in timeout={1800}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TimelineIcon color="primary" />
                      Engagement Trend
                    </Typography>
                    <Box sx={{ height: 150 }}>
                      <Line data={trendData} options={chartOptions} />
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grid>
        </Grid>

        {/* Monthly Engagement Chart */}
        <Grid item xs={12} lg={8}>
          <Slide direction="up" in timeout={2000}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  Monthly Engagement Overview
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Bar data={engagementChartData} options={chartOptions} />
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>

        {/* Top Performers */}
        <Grid item xs={12} lg={4}>
          <Fade in timeout={2200}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrophyIcon color="primary" />
                  Top Performers
                </Typography>
                <Box>
                  {mockAnalytics.topPerformers.map((performer, index) => (
                    <Box 
                      key={performer.name}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        mb: 2,
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: index === 0 ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.grey[500], 0.05),
                        border: index === 0 ? `1px solid ${alpha(theme.palette.warning.main, 0.3)}` : 'none',
                      }}
                    >
                      <Avatar 
                        sx={{ 
                          bgcolor: index === 0 ? theme.palette.warning.main : theme.palette.primary.main,
                          fontWeight: 'bold'
                        }}
                      >
                        {performer.avatar}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {performer.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {performer.role}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${performer.votes} votes`} 
                        size="small" 
                        color={index === 0 ? "warning" : "primary"}
                        variant={index === 0 ? "filled" : "outlined"}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12}>
          <Slide direction="up" in timeout={2400}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon color="primary" />
                  Recent Activity
                </Typography>
                <Grid container spacing={2}>
                  {mockAnalytics.recentActivity.map((activity, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Paper 
                        sx={{ 
                          p: 2, 
                          textAlign: 'center',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[4],
                          }
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          {activity.action}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {activity.user}
                        </Typography>
                        <Typography variant="caption" color="primary">
                          {activity.time}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      {/* Coming Soon Dialog */}
      <ComingSoon
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
        feature={comingSoonFeature}
      />
    </Container>
  );
};

export default Dashboard;
